import React, { useState, useMemo } from 'react';
import Section from '../ui/Section.jsx';
import KpiCell from '../ui/KpiCell.jsx';
import DailyHoursChart from '../ui/DailyHoursChart.jsx';
import Skeleton from '../ui/Skeleton.jsx';
import { fmtHours } from '../../utils/formatters.js';

function fmtDateShort(iso) {
  const d = new Date(iso + 'T12:00:00Z');
  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${M[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function workingDays(days) {
  return days.filter(d => {
    const dt = new Date(d.date + 'T12:00:00Z');
    const dow = dt.getUTCDay();
    return dow !== 0 && dow !== 6;
  }).length;
}

export default function TimeTracking({ project, loadingRedmine = false, redmineError = false }) {
  const tt = project.time_tracking || { this_month: { days: [] }, last_month: { days: [] } };
  const [which, setWhich] = useState('this');
  const current = which === 'this' ? tt.this_month : tt.last_month;
  const days = current.days || [];

  const totalHours = useMemo(() => days.reduce((s, d) => s + (d.hours || 0), 0), [days]);

  const elapsedDays = useMemo(() => {
    return which === 'this' ? days.filter(d => !d.future) : days;
  }, [days, which]);
  const wd = Math.max(1, workingDays(elapsedDays));
  const avg = totalHours / wd;

  const identifier = project.redmine_identifier;
  const redmineUrl = identifier
    ? `https://mine.everlabs.com/projects/${identifier}/time_entries`
    : `https://mine.everlabs.com/projects/${project.id}/time_entries`;

  // Hooks must run on every render, so the loading/error short-circuits come
  // AFTER all hooks above (Rules of Hooks). The values computed above are safe
  // with the empty-days fallback and simply aren't rendered in these branches.
  if (loadingRedmine) {
    return (
      <Section id="time" title="Time tracking" count="Loading…">
        <div className="rounded-lg border" style={{ background: '#fff', borderColor: '#ECEBE5' }}>
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: '#F0EFE9' }}>
            <Skeleton width={180} height={28} />
            <Skeleton width={160} height={14} />
          </div>
          <div className="grid grid-cols-2 border-b" style={{ borderColor: '#F0EFE9' }}>
            <div className="px-5 py-4">
              <Skeleton width="55%" height={12} />
              <div style={{ marginTop: 8 }}><Skeleton width="40%" height={20} /></div>
            </div>
            <div className="px-5 py-4">
              <Skeleton width="55%" height={12} />
              <div style={{ marginTop: 8 }}><Skeleton width="40%" height={20} /></div>
            </div>
          </div>
          <div className="px-5 py-4">
            <Skeleton width={100} height={12} />
            <div style={{ marginTop: 12 }}><Skeleton width="100%" height={120} /></div>
          </div>
        </div>
      </Section>
    );
  }

  if (redmineError) {
    return (
      <Section id="time" title="Time tracking" count="—">
        <div className="text-[12px]" style={{ color: '#9A9A95' }}>Live time data unavailable.</div>
      </Section>
    );
  }

  const ToggleBtn = ({ v, label }) => (
    <button
      onClick={() => setWhich(v)}
      className="px-3 py-1.5 text-[12px]"
      style={{
        background: which === v ? '#F2F1ED' : '#fff',
        color:      which === v ? '#1A1A1A' : '#6B6B68',
        fontWeight: which === v ? 500       : 400,
        border: 'none', cursor: 'pointer'
      }}
    >{label}</button>
  );

  return (
    <Section id="time" title="Time tracking" count="Defaults to current month">
      <div className="rounded-lg border" style={{ background: '#fff', borderColor: '#ECEBE5' }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: '#F0EFE9' }}>
          <div className="inline-flex rounded-md border" style={{ borderColor: '#E0DFD9' }}>
            <ToggleBtn v="this" label="This month" />
            <ToggleBtn v="last" label="Last month" />
          </div>
          <div className="text-[12px]" style={{ color: '#6B6B68' }}>
            <span style={{ color: '#1A1A1A', fontWeight: 500 }}>{current.label}</span>
            <span style={{ color: '#CCC', margin: '0 8px' }}>·</span>
            <span>{fmtDateShort(current.from)} – {fmtDateShort(current.to)}</span>
            {which === 'this' && <span style={{ color: '#888' }}> (month-to-date)</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 border-b" style={{ borderColor: '#F0EFE9' }}>
          <KpiCell label="Total tracked" value={`${fmtHours(totalHours)}h`} />
          <KpiCell label="Avg per working day" value={`${fmtHours(avg)}h`} sub={`across ${wd} working days`} last />
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[12px] uppercase tracking-wider" style={{ color: '#9A9A95', letterSpacing: '0.08em' }}>Daily hours</h4>
          </div>
          <DailyHoursChart days={days} />
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between text-[11.5px]"
             style={{ borderColor: '#F0EFE9', color: '#888' }}>
          <span>Pulled from Redmine</span>
          <a href={redmineUrl} target="_blank" rel="noopener noreferrer"
             className="hover:underline" style={{ color: '#3A3A38' }}>Open in Redmine →</a>
        </div>
      </div>
    </Section>
  );
}
